import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import InterviewDetailsCard from '../layout/InterviewDetailsCard';
import ConversationTranscript from '../layout/ConversationTranscript';

interface InterviewSidebarProps {
  isMobileView: boolean;
}

/**
 * InterviewSidebar - Right sidebar with interview details and transcript
 * 
 * This component is responsible for:
 * 1. Organizing the right sidebar content
 * 2. Managing visibility of interview details based on screen size
 * 3. Displaying the conversation transcript
 * 4. Showing message input on desktop
 */
const InterviewSidebar: React.FC<InterviewSidebarProps> = ({ isMobileView }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const toggleExpand = () => {
    if (isAnimating) return;
    
    setIsExpanded(!isExpanded);
    setIsAnimating(true);
    
    // Notify parent component about the change to adjust grid layout
    if (typeof window !== 'undefined') {
      const event = new CustomEvent('transcript-toggle', { 
        detail: { expanded: !isExpanded } 
      });
      window.dispatchEvent(event);
    }
  };

  // Reset animation state after transition completes
  useEffect(() => {
    if (isAnimating) {
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [isAnimating]);

  return (
    <div className="lg:col-span-1 lg:order-2 order-1 h-full min-h-0 flex flex-col overflow-hidden interview-layout-transition">
      <div className="flex flex-col space-y-3 h-full min-h-0 overflow-hidden">
        {/* Interview details card - hide on mobile */}
        {!isMobileView && (
          <InterviewDetailsCard />
        )}
        
        {/* Conversation Transcript Card */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden interview-layout-transition">
          {/* Add expand/collapse toggle button on mobile */}
          {isMobileView && (
            <button 
              onClick={toggleExpand}
              className="flex items-center justify-center p-2 bg-background-light/50 dark:bg-background-dark/50 rounded-t-lg border-t border-x border-border-light dark:border-border-dark interview-layout-transition"
              disabled={isAnimating}
            >
              <div className="interview-layout-transition" style={{ transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)' }}>
                <ChevronDown className="w-5 h-5 text-text-secondary-light dark:text-text-secondary-dark" />
              </div>
              <span className="ml-2 text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">
                {isExpanded ? "Collapse Messages" : "Expand Messages"}
              </span>
            </button>
          )}
          <ConversationTranscript isMobileView={isMobileView} />
        </div>
        
      </div>
    </div>
  );
};

export default InterviewSidebar; 